import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = loading, false = guest, obj = user
  const [token, setToken] = useState(() => localStorage.getItem("ekom1_token") || null);

  useEffect(() => {
    if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    else delete axios.defaults.headers.common["Authorization"];
  }, [token]);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      return data;
    } catch (err) {
      if (err?.response?.status !== 401) {
        console.warn("refreshMe failed:", err?.message);
      }
      setUser(false);
      return null;
    }
    // API is a module-level constant (process.env.REACT_APP_BACKEND_URL); setUser is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data.token) {
      localStorage.setItem("ekom1_token", data.token);
      setToken(data.token);
    }
    setUser(data);
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password });
    if (data.token) {
      localStorage.setItem("ekom1_token", data.token);
      setToken(data.token);
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); }
    catch (err) { console.warn("Logout request failed (likely already expired):", err?.response?.status); }
    // Purge local storage (jeton + éventuelles données sensibles)
    try {
      localStorage.removeItem("ekom1_token");
      // Nettoyer toute entrée commençant par ekom1_ (préférences personnelles)
      Object.keys(localStorage)
        .filter((k) => k.startsWith("ekom1_"))
        .forEach((k) => localStorage.removeItem(k));
      sessionStorage.clear();
    } catch (_) { /* ignore storage access errors */ }
    // Demander au Service Worker de purger les caches API et fichiers
    try {
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: "LOGOUT_PURGE" });
      }
    } catch (_) { /* ignore */ }
    setToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, refreshMe, API }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
